import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  Sparkles,
  Lock,
  AlertTriangle,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LS_KEY = (token) => `perception_submitted_${token}`;
const LS_DRAFT_KEY = (token) => `perception_draft_${token}`;

const AREA_NAMES = {
  A: "Marca empleadora",
  B: "Selección",
  C: "Onboarding",
  D: "Desempeño",
  E: "Feedback",
  F: "Desarrollo",
  G: "Cierre",
};

const TEXT_MAX = 800;

export default function PerceptionPublicTest() {
  const { token } = useParams();

  const [phase, setPhase] = useState("loading"); // loading | invalid | already | intro | wizard | review | submitting | thanks
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // {n: {kind, value?, text?}}
  const [step, setStep] = useState(0); // index in questions
  const [allowResubmit, setAllowResubmit] = useState(false);

  // --------- Bootstrap ---------
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (!token || token.length < 20) {
        setPhase("invalid");
        return;
      }
      // Anti-duplicado
      const submitted = localStorage.getItem(LS_KEY(token));
      if (submitted && !allowResubmit) {
        setPhase("already");
        return;
      }
      try {
        const r = await axios.get(`${API_BASE}/perception/public/${token}`);
        if (cancelled) return;
        const qs = r.data?.questions || [];
        setQuestions(qs);
        // Recuperar draft si existe
        try {
          const draft = JSON.parse(localStorage.getItem(LS_DRAFT_KEY(token)) || "{}");
          if (draft && typeof draft === "object") setAnswers(draft);
        } catch { /* ignore */ }
        setPhase("intro");
      } catch (err) {
        if (cancelled) return;
        setPhase("invalid");
      }
    }
    boot();
    return () => { cancelled = true; };
  }, [token, allowResubmit]);

  // --------- Persist draft ---------
  useEffect(() => {
    if (!token || phase === "loading" || phase === "invalid" || phase === "already") return;
    try {
      localStorage.setItem(LS_DRAFT_KEY(token), JSON.stringify(answers));
    } catch { /* ignore */ }
  }, [answers, token, phase]);

  const current = questions[step];

  const isClosedMissing = useCallback((q) => {
    if (q.kind !== "closed") return false;
    const a = answers[q.n];
    return !a || ![1, 2, 3, 4].includes(a.value);
  }, [answers]);

  const isRequiredOpenMissing = useCallback((q) => {
    if (q.kind !== "open" || !q.required) return false;
    const a = answers[q.n];
    return !a || !(a.text || "").trim();
  }, [answers]);

  const currentInvalid = useMemo(() => {
    if (!current) return false;
    return isClosedMissing(current) || isRequiredOpenMissing(current);
  }, [current, isClosedMissing, isRequiredOpenMissing]);

  const progressPct = useMemo(() => {
    if (!questions.length) return 0;
    return Math.round(((step + 1) / questions.length) * 100);
  }, [step, questions.length]);

  const answeredCount = useMemo(() => {
    return Object.values(answers).filter((a) => {
      if (a.kind === "closed") return [1, 2, 3, 4].includes(a.value);
      return !!(a.text || "").trim();
    }).length;
  }, [answers]);

  const missingRequired = useMemo(() => {
    return questions
      .filter((q) => isClosedMissing(q) || isRequiredOpenMissing(q))
      .map((q) => q.n);
  }, [questions, isClosedMissing, isRequiredOpenMissing]);

  // --------- Handlers ---------
  function setClosed(n, value) {
    setAnswers((prev) => ({ ...prev, [n]: { n, kind: "closed", value } }));
  }
  function setOpen(n, text) {
    setAnswers((prev) => ({ ...prev, [n]: { n, kind: "open", text } }));
  }
  function goNext() {
    if (currentInvalid) {
      toast.error(current.kind === "closed"
        ? "Elige una opción para continuar"
        : "Esta respuesta es obligatoria");
      return;
    }
    if (step < questions.length - 1) setStep(step + 1);
    else setPhase("review");
  }
  function goPrev() {
    if (step > 0) setStep(step - 1);
    else setPhase("intro");
  }

  async function submitAll() {
    if (missingRequired.length > 0) {
      toast.error(`Faltan respuestas obligatorias (${missingRequired.length}). Revísalas antes de enviar.`);
      return;
    }
    setPhase("submitting");
    try {
      // Solo enviamos respuestas que tengan valor real (para no llenar el server con nulos)
      const payload = {
        answers: Object.values(answers)
          .filter((a) => (a.kind === "closed" && [1, 2, 3, 4].includes(a.value)) || (a.kind === "open" && (a.text || "").trim()))
          .map((a) => ({ n: a.n, kind: a.kind, value: a.value, text: a.text })),
      };
      await axios.post(`${API_BASE}/perception/public/${token}/submit`, payload);
      try {
        localStorage.setItem(LS_KEY(token), new Date().toISOString());
        localStorage.removeItem(LS_DRAFT_KEY(token));
      } catch { /* ignore */ }
      setPhase("thanks");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || "No pudimos enviar tus respuestas. Inténtalo de nuevo.");
      setPhase("review");
    }
  }

  // --------- Render ---------
  return (
    <div className="min-h-screen relative" style={{ background: "linear-gradient(180deg, #f8faf9 0%, #eef4f1 100%)" }}>
      {/* Grain overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }} />
      <div className="relative">
        <MiniHeader />

        <main className="mx-auto max-w-3xl px-4 pt-8 pb-16">
          {phase === "loading" && <LoadingBlock />}
          {phase === "invalid" && <InvalidBlock />}
          {phase === "already" && <AlreadyBlock onResubmit={() => setAllowResubmit(true)} />}
          {phase === "intro" && (
            <IntroBlock
              onStart={() => setPhase("wizard")}
              answeredCount={answeredCount}
              totalCount={questions.length}
            />
          )}
          {phase === "wizard" && current && (
            <WizardBlock
              question={current}
              step={step}
              total={questions.length}
              progressPct={progressPct}
              answer={answers[current.n]}
              onClosed={(v) => setClosed(current.n, v)}
              onOpen={(t) => setOpen(current.n, t)}
              onPrev={goPrev}
              onNext={goNext}
              invalid={currentInvalid}
            />
          )}
          {phase === "review" && (
            <ReviewBlock
              questions={questions}
              answers={answers}
              missing={missingRequired}
              onBack={() => setPhase("wizard")}
              onEdit={(n) => {
                const idx = questions.findIndex((q) => q.n === n);
                if (idx >= 0) { setStep(idx); setPhase("wizard"); }
              }}
              onSubmit={submitAll}
              answeredCount={answeredCount}
            />
          )}
          {phase === "submitting" && <SubmittingBlock />}
          {phase === "thanks" && <ThanksBlock />}
        </main>

        <FooterBrand />
      </div>
    </div>
  );
}

// ============ Blocks ============

function MiniHeader() {
  return (
    <header className="border-b border-emerald-900/5 bg-white/60 backdrop-blur-sm">
      <div className="mx-auto max-w-3xl px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}>
            <Sparkles className="w-4 h-4" style={{ color: "#E5C874" }} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-900/60">Test de percepción</div>
            <div className="text-sm font-semibold text-slate-800">Voz anónima del equipo</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-emerald-800/70">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Anónimo · sin login</span>
        </div>
      </div>
    </header>
  );
}

function FooterBrand() {
  return (
    <footer className="border-t border-emerald-900/5 mt-8">
      <div className="mx-auto max-w-3xl px-4 py-6 text-center text-xs text-slate-500">
        OptimizaRRHH · Talent Health Check
        <div className="mt-1 text-slate-400">Tus respuestas son anónimas. La dirección solo verá patrones agregados.</div>
      </div>
    </footer>
  );
}

function LoadingBlock() {
  return (
    <div className="text-center py-24" data-testid="perception-public-loading">
      <Loader2 className="w-8 h-8 text-emerald-700 animate-spin mx-auto mb-3" />
      <p className="text-sm text-slate-500">Cargando test…</p>
    </div>
  );
}

function InvalidBlock() {
  return (
    <div className="rounded-3xl bg-white border border-red-200 p-8 md:p-10 shadow-sm" data-testid="perception-public-invalid">
      <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-5">
        <Lock className="w-7 h-7" />
      </div>
      <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 mb-3">Enlace no válido o expirado</h1>
      <p className="text-slate-600 leading-relaxed">
        El enlace que estás usando no está activo. Puede que la persona que te lo envió lo haya regenerado.
        Contacta con quien te compartió el enlace para recibir el nuevo.
      </p>
    </div>
  );
}

function AlreadyBlock({ onResubmit }) {
  return (
    <div className="rounded-3xl bg-white border border-emerald-200 p-8 md:p-10 shadow-sm" data-testid="perception-public-already-done">
      <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-5">
        <CheckCircle2 className="w-7 h-7" />
      </div>
      <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 mb-3">Ya has respondido este test</h1>
      <p className="text-slate-600 leading-relaxed mb-6">
        Desde este navegador ya se ha enviado una respuesta. Gracias por tu tiempo.
      </p>
      <p className="text-sm text-slate-500 mb-4">
        Si compartes ordenador con un compañero y él aún no ha respondido, puedes enviar una nueva respuesta.
      </p>
      <button
        onClick={onResubmit}
        className="rounded-full px-5 py-2.5 text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-all"
        data-testid="perception-public-resubmit-btn"
      >
        Enviar otra respuesta
      </button>
    </div>
  );
}

function IntroBlock({ onStart, answeredCount, totalCount }) {
  const hasDraft = answeredCount > 0;
  return (
    <div className="space-y-6" data-testid="perception-public-intro">
      {/* Hero */}
      <div className="rounded-3xl relative overflow-hidden p-8 md:p-10 text-white" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 60%, #144d3a 100%)" }}>
        <div className="absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle at center, #E5C874 0%, transparent 70%)" }} />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: "#E5C874" }}>Test de contraste · anónimo</div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold mt-3 leading-tight">Tu voz cuenta.</h1>
          <p className="mt-4 text-white/85 max-w-xl leading-relaxed text-base md:text-lg">
            La dirección de tu empresa quiere entender de verdad cómo se gestiona el talento aquí desde tu experiencia. Este test es 100% anónimo y solo tarda unos 8 minutos.
          </p>
        </div>
      </div>

      {/* Reassurance cards */}
      <div className="grid md:grid-cols-3 gap-3">
        <ReassureCard
          icon={<ShieldCheck className="w-4 h-4" />}
          title="Anónimo de verdad"
          text="Nadie verá tus respuestas individuales. Solo se comparten patrones agregados del equipo."
        />
        <ReassureCard
          icon={<Sparkles className="w-4 h-4" />}
          title="Honestidad, no examen"
          text="No hay respuestas buenas ni malas. Nos interesa lo que realmente vives, no lo que 'debería' decirse."
        />
        <ReassureCard
          icon={<CheckCircle2 className="w-4 h-4" />}
          title="20 preguntas · 8 minutos"
          text="12 opciones múltiples + 8 espacios para escribir. La última es obligatoria; el resto no."
        />
      </div>

      {/* CTA */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
        <div>
          <h2 className="font-display text-lg md:text-xl font-semibold text-slate-900">
            {hasDraft ? "Continúa donde lo dejaste" : "Empieza cuando quieras"}
          </h2>
          {hasDraft ? (
            <p className="text-sm text-slate-600 mt-1">Tienes {answeredCount} de {totalCount} preguntas ya respondidas en este navegador.</p>
          ) : (
            <p className="text-sm text-slate-600 mt-1">Puedes cerrar y volver: se guarda un borrador local en tu navegador.</p>
          )}
        </div>
        <button
          onClick={onStart}
          className="rounded-full px-7 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 inline-flex items-center gap-2 self-start md:self-auto"
          style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
          data-testid="perception-public-intro-start"
        >
          {hasDraft ? "Continuar" : "Comenzar"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function ReassureCard({ icon, title, text }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2">{icon}</div>
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 mt-1 leading-relaxed">{text}</div>
    </div>
  );
}

function WizardBlock({ question, step, total, progressPct, answer, onClosed, onOpen, onPrev, onNext, invalid }) {
  const isClosed = question.kind === "closed";
  const isRequired = question.kind === "open" && question.required;
  const areaLabel = AREA_NAMES[question.area] || question.area;

  return (
    <div className="space-y-5" data-testid={`perception-public-q-${question.n}`}>
      {/* Progress */}
      <div>
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
          <span className="font-semibold uppercase tracking-[0.14em] text-emerald-800/80">[{question.area}] {areaLabel}</span>
          <span>Pregunta {step + 1} de {total}</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressPct}%`, background: "linear-gradient(90deg, #0F6E56, #C9A84C)" }} />
        </div>
      </div>

      {/* Question card */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
        <h2 className="font-display text-xl md:text-2xl text-slate-900 leading-snug">
          {question.text}
          {isRequired && <span className="text-red-500 ml-1" title="Obligatoria">*</span>}
        </h2>

        {isClosed ? (
          <div className="mt-6 space-y-2.5">
            {question.options.map((opt, idx) => {
              const value = idx + 1;
              const selected = answer?.value === value;
              return (
                <button
                  key={idx}
                  onClick={() => onClosed(value)}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${
                    selected
                      ? "border-[#0F6E56] bg-[#0F6E56]/5 ring-2 ring-[#0F6E56]/20"
                      : "border-slate-200 hover:border-[#0F6E56]/40 hover:bg-slate-50"
                  }`}
                  data-testid={`perception-public-q-${question.n}-opt-${value}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center border-2 ${selected ? "border-[#0F6E56] bg-[#0F6E56]" : "border-slate-300"}`}>
                      {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div className="text-sm text-slate-800 leading-relaxed">{opt}</div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="mt-6">
            <textarea
              value={answer?.text || ""}
              onChange={(e) => onOpen(e.target.value.slice(0, TEXT_MAX))}
              placeholder={isRequired ? "Tu respuesta (obligatoria)…" : "Tu respuesta (opcional)…"}
              rows={6}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/30 focus:border-[#0F6E56] transition-all resize-none"
              data-testid={`perception-public-q-${question.n}-text`}
            />
            <div className="flex items-center justify-between mt-1.5 text-xs">
              <span className={isRequired ? "text-red-600" : "text-slate-400"}>
                {isRequired ? "Obligatoria" : "Opcional · puedes dejarla en blanco"}
              </span>
              <span className="text-slate-400">{(answer?.text || "").length}/{TEXT_MAX}</span>
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onPrev}
          className="rounded-full px-5 py-2.5 text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-white transition-all inline-flex items-center gap-2"
          data-testid="perception-public-prev"
        >
          <ArrowLeft className="w-4 h-4" />Anterior
        </button>
        <button
          onClick={onNext}
          disabled={invalid && (question.kind === "closed" || question.required)}
          className="rounded-full px-6 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
          data-testid="perception-public-next"
        >
          {step === total - 1 ? "Revisar" : "Siguiente"}<ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {question.kind === "open" && !question.required && (
        <div className="text-xs text-center text-slate-400">
          Esta pregunta es opcional. Puedes pasar directamente a la siguiente sin escribir nada.
        </div>
      )}
    </div>
  );
}

function ReviewBlock({ questions, answers, missing, onBack, onEdit, onSubmit, answeredCount }) {
  return (
    <div className="space-y-5" data-testid="perception-public-review">
      <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0"><CheckCircle2 className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900">Revisa antes de enviar</h2>
            <p className="text-sm text-slate-600 mt-1">Has completado {answeredCount} de {questions.length} preguntas. Solo la última es obligatoria; el resto puedes dejarlas en blanco si lo prefieres.</p>
          </div>
        </div>

        {missing.length > 0 && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">
              Faltan {missing.length} {missing.length === 1 ? "respuesta obligatoria" : "respuestas obligatorias"}. Corrígelas antes de enviar.
            </div>
          </div>
        )}

        <ul className="divide-y divide-slate-100">
          {questions.map((q) => {
            const a = answers[q.n];
            const filled = q.kind === "closed"
              ? [1, 2, 3, 4].includes(a?.value)
              : !!(a?.text || "").trim();
            const isMissing = missing.includes(q.n);
            return (
              <li key={q.n} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-800/70">[{q.area}] {q.n}</span>
                    {isMissing && <span className="text-[10px] font-semibold text-red-600">FALTA</span>}
                    {!isMissing && !filled && q.kind === "open" && !q.required && <span className="text-[10px] text-slate-400">sin respuesta (opcional)</span>}
                  </div>
                  <div className="text-sm text-slate-700 line-clamp-2">{q.text}</div>
                  {filled && (
                    <div className="text-xs text-slate-500 mt-1 italic line-clamp-1">
                      {q.kind === "closed" ? q.options[a.value - 1] : `"${a.text}"`}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onEdit(q.n)}
                  className="text-xs font-semibold text-[#0F6E56] hover:underline flex-shrink-0 mt-0.5"
                  data-testid={`perception-public-edit-${q.n}`}
                >
                  Editar
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="rounded-full px-5 py-2.5 text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-white transition-all inline-flex items-center gap-2"
          data-testid="perception-public-back-to-wizard"
        >
          <ArrowLeft className="w-4 h-4" />Volver
        </button>
        <button
          onClick={onSubmit}
          disabled={missing.length > 0}
          className="rounded-full px-7 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
          data-testid="perception-public-submit"
        >
          Enviar respuestas<Sparkles className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SubmittingBlock() {
  return (
    <div className="text-center py-24" data-testid="perception-public-submitting">
      <Loader2 className="w-10 h-10 text-emerald-700 animate-spin mx-auto mb-4" />
      <p className="text-slate-700 font-medium">Enviando tus respuestas…</p>
      <p className="text-xs text-slate-500 mt-1">Un momento por favor</p>
    </div>
  );
}

function ThanksBlock() {
  return (
    <div className="space-y-6" data-testid="perception-public-thanks">
      <div className="rounded-3xl relative overflow-hidden p-8 md:p-12 text-white text-center" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 60%, #144d3a 100%)" }}>
        <div className="absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle at center, #E5C874 0%, transparent 70%)" }} />
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8" style={{ color: "#E5C874" }} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: "#E5C874" }}>Respuestas enviadas</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-3 leading-tight">Gracias por tu tiempo.</h1>
          <p className="mt-4 text-white/85 max-w-xl mx-auto leading-relaxed">
            Tu voz se une, de forma anónima, a la del resto del equipo. La dirección recibirá un patrón agregado — nunca respuestas individuales — para tomar mejores decisiones sobre cómo se gestiona el talento.
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8">
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-2">¿Qué pasa ahora?</h2>
        <ul className="space-y-2 text-sm text-slate-600 leading-relaxed">
          <li className="flex gap-2"><span className="text-emerald-700 mt-0.5">•</span>Cuando haya suficientes respuestas del equipo, se generará un resumen anónimo automáticamente.</li>
          <li className="flex gap-2"><span className="text-emerald-700 mt-0.5">•</span>La dirección lo contrasta con su propio informe para descubrir puntos ciegos o alarmas infundadas.</li>
          <li className="flex gap-2"><span className="text-emerald-700 mt-0.5">•</span>Nadie verá tus respuestas individualmente. No se guarda tu identidad, IP ni email.</li>
        </ul>
      </div>

      <div className="text-center text-xs text-slate-400">
        Ya puedes cerrar esta pestaña.
      </div>
    </div>
  );
}
